import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';

const StyledMuiButton = styled(MuiButton)({
  textTransform: 'none',
});

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  /** react-toolbox: flat button style */
  flat?: boolean;
  /** react-toolbox: raised button style */
  raised?: boolean;
  /** react-toolbox: floating action button style */
  floating?: boolean;
  /** react-toolbox: button label text */
  label?: React.ReactNode;
  /** react-toolbox: accent color */
  accent?: boolean;
  /** react-toolbox: primary color */
  primary?: boolean;
  /** react-toolbox: neutral/default styling */
  neutral?: boolean;
  /** react-toolbox: mini size for floating buttons */
  mini?: boolean;
  /** react-toolbox: ripple effect (inverted - false means disable) */
  ripple?: boolean;
  /** react-toolbox: icon name or element */
  icon?: React.ReactNode;
  /** MUI variant override */
  variant?: MuiButtonProps['variant'];
}

/**
 * Button wrapper - maps react-toolbox Button to MUI Button
 * - `flat` maps to variant="text"
 * - `raised` maps to variant="contained"
 * - `floating` maps to variant="contained" (FAB-like)
 * - `accent` maps to color="secondary"
 * - `primary` maps to color="primary"
 * - `ripple={false}` maps to disableRipple
 * - `label` maps to button content alongside children
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      flat,
      raised,
      floating,
      label,
      accent,
      primary,
      neutral,
      mini,
      ripple = true,
      icon,
      children,
      variant: variantProp,
      color: colorProp,
      ...rest
    },
    ref,
  ) => {
    // Determine variant
    let variant: MuiButtonProps['variant'] = variantProp;
    if (!variant) {
      if (flat) {
        variant = 'text';
      } else if (raised || floating) {
        variant = 'contained';
      } else {
        variant = 'text'; // default for react-toolbox
      }
    }

    // Determine color
    let color: MuiButtonProps['color'] = colorProp;
    if (!color) {
      if (accent) {
        color = 'secondary';
      } else if (primary) {
        color = 'primary';
      } else if (neutral) {
        color = 'inherit';
      }
    }

    // Handle ripple (react-toolbox ripple={false} means disable)
    const disableRipple = ripple === false;

    return (
      <StyledMuiButton
        ref={ref}
        variant={variant}
        color={color}
        disableRipple={disableRipple}
        size={mini ? 'small' : undefined}
        startIcon={icon}
        {...rest}
      >
        {label}
        {children}
      </StyledMuiButton>
    );
  },
);

Button.displayName = 'Button';

export default Button;
